<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.addOffer") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #e1423d">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs" novalidate>
        <div class="row">
          <!-- Start:: Main Category Select Input -->
          <base-select-input
            col="6"
            :optionsList="categoriesList"
            :placeholder="$t('PLACEHOLDERS.mainCategory')"
            v-model="data.category_id"
            @input="handleCategoryChange"
            required
          />
          <!-- End:: Main Category Select Input -->

          <!-- Start:: Product Select Input -->
          <base-select-input
            col="6"
            :optionsList="productsList"
            :placeholder="$t('PLACEHOLDERS.productName')"
            v-model="data.product_id"
            @input="handleProductChange"
            :disabled="!data.category_id"
            required
          />
          <!-- End:: Product Select Input -->

          <!-- Start:: Product Prices List -->
          <div class="col-12" v-if="selectedProduct && selectedProduct.prices">
            <div class="prices-section-header mb-4">
              <h5 class="mb-0" style="color: #e1423d; font-weight: 600">
                <i class="fas fa-dollar-sign mr-2"></i>
                {{ $t("PLACEHOLDERS.productPrice") }}
              </h5>
            </div>

            <div class="prices-list">
              <div
                v-for="(price, index) in selectedProduct.prices"
                :key="index"
                class="price-card mb-4"
              >
                <div class="price-card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <label class="price-field-label">
                        {{ $t("PLACEHOLDERS.originalPrice") }}:
                      </label>
                      <p
                        style="font-size: 1.1rem; font-weight: 600; color: #333"
                      >
                        {{ price.price }} {{ $t("PLACEHOLDERS.riyal") }}
                      </p>
                      <p
                        style="font-size: 1.1rem; font-weight: 600; color: #333"
                      >
                        {{ $t("PLACEHOLDERS.quantity") }} : {{ price.quantity }} 
                      </p>
                    </div>

                    <div class="col-md-4">
                      <label class="price-field-label">
                        {{ $t("PLACEHOLDERS.discountPercentage") }}:
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="
                          data.product_prices[index].discount_percentage
                        "
                        :placeholder="$t('PLACEHOLDERS.discountPercentage')"
                        min="0"
                        max="100"
                        step="0.01"
                        style="border: 1px solid #e1423d"
                      />
                    </div>

                    <div
                      class="col-md-4"
                      v-if="
                        data.product_prices[index] &&
                        data.product_prices[index].discount_percentage
                      "
                    >
                      <label class="price-field-label">
                        {{ $t("PLACEHOLDERS.priceAfterDiscount") }}:
                      </label>
                      <p
                        style="
                          font-size: 1.1rem;
                          font-weight: 600;
                          color: #e1423d;
                        "
                      >
                        {{
                          calculateDiscountedPrice(
                            price.price,
                            data.product_prices[index].discount_percentage
                          )
                        }}
                        {{ $t("PLACEHOLDERS.riyal") }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- End:: Product Prices List -->

          <!-- Start:: Discount Percentage Input -->
          <!-- <base-input
            col="12"
            type="number"
            :placeholder="$t('PLACEHOLDERS.discountPercentage')"
            v-model.number="data.discount_percentage"
            :min="1"
            :max="100"
            step="0.01"
            required
          >
            <template slot="append">
              <span style="color: #E1423D; font-weight: 600;">%</span>
            </template>
          </base-input> -->
          <!-- End:: Discount Percentage Input -->

          <!-- Start:: Start Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledStartDate"
            :placeholder="$t('PLACEHOLDERS.offerStartDate')"
            v-model.trim="data.start_date"
            required
          />
          <!-- End:: Start Date Input -->

          <!-- Start:: End Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledEndDate"
            :placeholder="$t('PLACEHOLDERS.offerEndDate')"
            v-model.trim="data.end_date"
            required
          />
          <!-- End:: End Date Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
export default {
  name: "CreateOffer",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        category_id: null,
        product_id: null,
        product_prices: [],
        start_date: null,
        end_date: null,
        is_active: true,
      },
      // End:: Data Collection To Send

      categoriesList: [],
      productsList: [],
      selectedProduct: null,
    };
  },

  computed: {
    // Disable dates before today for start date
    disabledStartDate() {
      return (current) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return current && current < today;
      };
    },

    // Disable dates before start date for end date
    disabledEndDate() {
      return (current) => {
        if (!this.data.start_date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return current && current < today;
        }
        const startDate = new Date(this.data.start_date);
        startDate.setHours(0, 0, 0, 0);
        return current && current < startDate;
      };
    },
  },

  methods: {
    // Start:: Handle Category Change
    async handleCategoryChange() {
      this.data.product_id = null;
      this.selectedProduct = null;
      this.productsList = [];
      this.data.product_prices = [];

      if (this.data.category_id) {
        await this.getProducts();
      }
    },
    // End:: Handle Category Change

    // Start:: Handle Product Change
    async handleProductChange() {
      if (this.data.product_id) {
        await this.getProductDetails();
      } else {
        this.selectedProduct = null;
        this.data.product_prices = [];
      }
    },
    // End:: Handle Product Change

    // Start:: Get Categories
    async getCategories() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "product-categories?paginate=false&filter[is_active]=true",
          params: {
            limit: 0,
            page: 0,
          },
        });
        this.categoriesList = (res.data.data?.data || res.data.data || []).map(
          (category) => ({
            id: category.id,
            name: category.name,
          })
        );
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Categories

    // Start:: Get Products
    async getProducts() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "products?paginate=false&filter[is_active]=true",
          params: {
            "filter[category_id]": this.data.category_id?.id,
            limit: 0,
            page: 0,
          },
        });
        this.productsList = (res.data.data?.data || res.data.data || []).map(
          (product) => ({
            id: product.id,
            name: product.name || product.name_ar,
          })
        );
      } catch (error) {
        console.log(error.response?.data?.message);
        this.productsList = [];
      }
    },
    // End:: Get Products

    // Start:: Get Product Details
    async getProductDetails() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `products/${this.data.product_id.id}`,
        });
        this.selectedProduct = res.data.data;

        // Initialize product_prices array with product's prices
        if (this.selectedProduct && this.selectedProduct.prices) {
          this.data.product_prices = this.selectedProduct.prices.map(
            (price) => ({
              product_price_id: price.id,
              discount_percentage: null,
            })
          );
        }
      } catch (error) {
        console.log(error.response?.data?.message);
        this.selectedProduct = null;
        this.data.product_prices = [];
      }
    },
    // End:: Get Product Details

    // Start:: Calculate Discounted Price
    calculateDiscountedPrice(originalPrice, discountPercentage) {
      if (!discountPercentage || !originalPrice) {
        return originalPrice;
      }
      const discount = (originalPrice * discountPercentage) / 100;
      return (originalPrice - discount).toFixed(2);
    },
    // End:: Calculate Discounted Price

    // Start:: Validate Form Inputs
    validateFormInputs() {
      // Validate product prices
      const hasAnyDiscount = this.data.product_prices.some(
        (price) => price.discount_percentage && price.discount_percentage > 0
      );

      if (!hasAnyDiscount) {
        this.$message.error(
          this.$t("VALIDATION.atLeastOneDiscountRequired") ||
            "At least one discount percentage is required"
        );
        this.isWaitingRequest = false;
        return;
      }

      // Validate discount percentages
      for (let price of this.data.product_prices) {
        if (
          price.discount_percentage &&
          (price.discount_percentage <= 0 || price.discount_percentage > 100)
        ) {
          this.$message.error(
            this.$t("VALIDATION.discountPercentageRange") ||
              "Discount percentage must be between 1 and 100"
          );
          this.isWaitingRequest = false;
          return;
        }
      }

      // Validate dates
      if (!this.data.start_date) {
        this.$message.error(
          this.$t("VALIDATION.startDateRequired") || "Start date is required"
        );
        this.isWaitingRequest = false;
        return;
      }

      if (!this.data.end_date) {
        this.$message.error(
          this.$t("VALIDATION.endDateRequired") || "End date is required"
        );
        this.isWaitingRequest = false;
        return;
      }

      const startDate = new Date(this.data.start_date);
      const endDate = new Date(this.data.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        this.$message.error(
          this.$t("VALIDATION.startDateMustBeTodayOrLater") ||
            "Start date must be today or later"
        );
        this.isWaitingRequest = false;
        return;
      }

      if (endDate < startDate) {
        this.$message.error(
          this.$t("VALIDATION.endDateMustBeAfterStartDate") ||
            "End date must be equal to or after start date"
        );
        this.isWaitingRequest = false;
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: Validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      try {
        // Prepare product_prices data in the required format
        const product_prices = this.data.product_prices
          .filter(
            (price) =>
              price.discount_percentage && price.discount_percentage > 0
          )
          .map((price) => ({
            product_price_id: price.product_price_id,
            discount_percentage: price.discount_percentage,
          }));

        const formData = {
          product_id: this.data.product_id?.id,
          start_date: this.data.start_date,
          end_date: this.data.end_date,
          is_active: this.data.is_active,
          product_prices: product_prices,
        };

        await this.$axios({
          method: "POST",
          url: "offers",
          data: formData,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/offers/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Submit Form
  },

  created() {
    // Start:: Fire Methods
    this.getCategories();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.prices-section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1423d;
}

.price-card {
  border: 1px solid #e1423d;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: hidden;
}

.price-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #e1423d;
}

.price-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e1423d;
}

.price-card-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
}

.price-card-body {
  padding: 1.25rem;
}

.price-field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
}
</style>
